import React, { useCallback, useEffect, useState } from 'react';
import DestinationsSide from 'components/DestinationsSide';
import SideBar from 'components/SideBar';
import JourneyInfoSide from 'components/JourneyInfoSide';
import Spinner from 'components/Spinner';
import StopsDetails from 'components/StopsDetails';
import OfferActionsDetail from 'components/OfferActionsDetail';
import Price from 'components/Price';
import {
  getOfferDetail,
  getDriverOfferDetail,
  fetcher,
} from 'utils/network/http';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ROUTES } from 'utils/constants/routes';
import { useAuth } from 'hooks/useAuth/useAuth';
import { useAlert } from 'hooks/useAlert/useAlert';
import useSWR from 'swr';
import config from 'utils/constants/config';
import { getSource, sendEventToSegment } from 'utils/functions/Tracking';
import trackingEvents from '../../utils/constants/trackingEvents';

const OfferDetail = ({ className, ...rest }) => {
  const { t } = useTranslation('OfferDetail');
  const { profile } = useAuth();
  const { id } = useParams();
  const location = useLocation();
  const [eventTriggered, setEventTriggered] = useState(false);

  const offerDetailUrl =
    location.state?.source && location.state.source === 'webpush'
      ? getDriverOfferDetail(id)
      : getOfferDetail(id);

  const { data: response, error, mutate: refetchOfferDetail } = useSWR(
    profile ? offerDetailUrl : null,
    fetcher,
    {
      refreshInterval: config.FAST_POLLING_DELAY,
    }
  );

  let history = useHistory();

  useEffect(() => {
    if (!eventTriggered && response && response.data) {
      const offer = response.data;
      let { source } = location.state || {};
      source = getSource(source, null);
      sendEventToSegment(trackingEvents.OFFER_DETAIL_VIEWED, {
        source: source || null,
        driver_offer_id: offer.offer_id,
      });
      history.replace({ state: { ...location.state, source: null } });
      setEventTriggered(true);
    }
  }, [eventTriggered, response, location, history]);

  const { openAlert, setText } = useAlert();

  const isCompliant = (offer) =>
    offer.compliance ? offer.compliance.is_compliant : true;

  const missingDocuments = (offer) =>
    offer.compliance && offer.compliance.document_type_ids;

  const renderOffer = useCallback(() => {
    const offer = response?.data;
    if (!offer && !error) {
      return (
        <div
          className="offer-detail__loading"
          data-testid="offer-detail__loading"
        >
          <Spinner />
        </div>
      );
    }

    if (error) {
      const { journey_status, journey_id, offer_status } = error.response.data;
      if (offer_status === 'OFFER_ASSIGNED_TO_YOU') {
        if (journey_status === 'ASSIGNED') {
          history.push(`${ROUTES.PENDING_DETAIL.DEFAULT}/${journey_id}`);
        } else if (journey_status === 'IN_TRANSIT') {
          history.push(`${ROUTES.IN_TRANSIT_DETAIL.DEFAULT}/${journey_id}`);
        } else {
          history.push(ROUTES.DONE);
        }
        return null;
      } else {
        setText('noOfferFound');
        openAlert();
        history.push(ROUTES.OFFERS);
        return null;
      }
    }

    if (offer) {
      const bidOffer =
        offer.acceptance_status && offer.acceptance_status.status === 'BID';
      const offerAmount = offer.acceptance_status
        ? offer.acceptance_status.cost
        : offer.net_full_cost;

      return (
        <>
          <SideBar backLabel={t('back')} redirectRoute={ROUTES.OFFERS}>
            <JourneyInfoSide journey={offer} />
            <div className="offer-detail__side-block">
              <DestinationsSide
                start_time_from={offer.start_time_from}
                start_time_from_tz={offer.start_time_from_tz}
                start_time_to={offer.start_time_to}
                start_time_to_tz={offer.start_time_to_tz}
                label_from={offer.label_from}
                country_from={offer.country_from}
                end_time_from={offer.end_time_from}
                end_time_from_tz={offer.end_time_from_tz}
                end_time_to={offer.end_time_to}
                end_time_to_tz={offer.end_time_to_tz}
                label_to={offer.label_to}
                country_to={offer.country_to}
              />
              <Price
                price={offer.net_full_cost}
                currency={offer.currency}
                showForSide
                showPrice={parseFloat(offer.net_full_cost) > 0.0}
                vat={offer.gross_full_cost}
              />
            </div>
            <OfferActionsDetail
              offerId={offer.offer_id}
              metaId={offer.id}
              offerCurrency={offer.currency}
              biddableOffer={offer.is_bidding_enabled}
              offerAmount={offerAmount}
              requested={offer.requested}
              bidOffer={bidOffer}
              refetchOffer={refetchOfferDetail}
              compliant={isCompliant(offer)}
              missing_documents={missingDocuments(offer)}
              carrierUuid={profile?.carrier?.uuid}
            />
          </SideBar>
          <div className="offer-detail__body">
            <StopsDetails journey={offer} hideSupportButton />
          </div>
        </>
      );
    }
  }, [
    error,
    history,
    openAlert,
    response,
    setText,
    t,
    refetchOfferDetail,
    profile,
  ]);

  return (
    <div className={className} data-testid="offer-detail">
      <div className="offer-detail" data-testid="offer-detail__container">
        {renderOffer()}
      </div>
    </div>
  );
};

export default OfferDetail;
